import type { ParentChildQuizAttempt } from '@repo/types/parent';

import { format, parseISO } from 'date-fns';

import { ParentGradeValue } from '@/components/features/parent/ParentGradeValue';
import QAttemptResultCard from '@/components/features/quizzes/QAttemptResultCard';
import { useParentDetailDialog } from '@/hooks/parent';

interface ParentQuizAttemptCardProps {
  attempt: ParentChildQuizAttempt;
  childId: number;
}

/**
 * One completed quiz of a child: which quiz, when it was finished, and how it
 * went.
 *
 * The score reuses the same result card the student's quiz list shows, so a
 * parent reads the result exactly as their child does. The attempt body is not
 * part of the parent payload — the outcome is visible, the answers are not — so
 * the dialog this opens adds the quiz's own terms and description rather than
 * any of their working.
 *
 * Every attempt a parent can see is completed, so the grade slot carries the
 * whole status on its own: a dash means the teacher has not marked it yet.
 *
 * Opening a row names it in the URL, which the page's dialog reads.
 */
export function ParentQuizAttemptCard(parentQuizAttemptCardProps: ParentQuizAttemptCardProps) {
  const { attempt, childId } = parentQuizAttemptCardProps;
  const { quiz, score, completedAt, grade } = attempt;

  const { setOpen } = useParentDetailDialog('quiz', childId, attempt.id);

  return (
    <li className={'border-border-primary bg-card/75 rounded-md border'}>
      {/* A real button, so the whole row is reachable by Tab and opens on
          Enter or Space without any key handling of our own. */}
      <button
        type={'button'}
        aria-haspopup={'dialog'}
        onClick={() => setOpen(true)}
        className={'outline-focus-ring hover:bg-accent/40 flex w-full flex-wrap items-center gap-x-3 gap-y-2 rounded-md p-3 text-left outline-0 transition-colors focus-visible:outline-3'}
      >
        <div className={'flex min-w-0 flex-1 flex-col'}>
          <span className={'text-foreground truncate text-sm font-medium'}>{quiz.name}</span>
          {completedAt ? <span className={'text-muted-foreground text-xs'}>{format(parseISO(completedAt), 'd MMM yyyy')}</span> : null}
        </div>

        <div className={'flex shrink-0 items-center gap-3'}>
          <QAttemptResultCard score={score ?? null} maxPoints={quiz.points} className={'w-auto'} />
          <ParentGradeValue grade={grade} />
        </div>

        {/* The row's own text names the quiz; this only says what activating it
            does, which nothing visible would otherwise. */}
        <span className={'sr-only'}>Pokaż szczegóły quizu</span>
      </button>
    </li>
  );
}
